import { useCallback } from 'react'
import { getResizeCursorForHandle } from '@/lib/transforms/selection-resize-cursor'
import { clampResizeToPasteboardMax } from '../gestures/pasteboard-clamp'
import { applyLiveGroupResize } from '../gestures/group-resize'
import { applyLiveMoveUpdates, computeMoveSmartGuideSnap } from '../gestures/move-gesture'
import { applyPhotoFillResizeClamp } from '../gestures/photo-fill-resize'
import { applyMultiSelectResizeStep, computeSingleObjectResize } from '../gestures/resize-gesture'
import {
  type MultiRotateInteraction,
  applyLiveMultiRotate,
  applyLiveSingleRotate,
} from '../gestures/rotate-gesture'
import { isPastMoveClickThreshold } from '../helpers/is-past-move-click-threshold'
import type { InteractionState } from '../types/interaction-state'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { SnapRefsApi } from './use-snap-refs'
import type { ShapeUpdatesApi } from './use-shape-updates'
import type { SmartGuidesApi } from './use-smart-guides'
import type { GestureSnappingApi } from './use-gesture-snapping'

export function useLiveGestureUpdates(
  ctx: ShapeManagerStateApi & SnapRefsApi & ShapeUpdatesApi & SmartGuidesApi & GestureSnappingApi
) {
  const {
    resizeMaxDimensionToastActiveRef,
    unitOfMeasure,
    effectiveSnapGuideDistance,
    emitSmartGuides,
    getGuideSnappedMoveDelta,
    getMovedSelectionBounds,
    getOtherShapeBounds,
    interactionRef,
    layoutSnapLines,
    multiSelectChromeRef,
    multiSelectSyncPrevShapesRef,
    panelDimensionsLinkedRef,
    pendingPointerRef,
    promoteAltDragDuplicateRef,
    rafIdRef,
    scaleRef,
    scheduleLiveSelectionGeometrySync,
    scheduleLiveWrapSync,
    setMultiSelectChrome,
    shapesRef,
    smartGuidesEnabledRef,
    snapToGuidesRef,
    updateShapeRef,
  } = ctx

  /** Move uses the latest pointer on every `pointermove` so chrome and shapes stay aligned (BUG 1624). */
  const applyLiveMove = useCallback(() => {
    const latest = pendingPointerRef.current
    const { current } = interactionRef

    if (!latest || !current || current.type !== 'move') {
      return
    }

    let snappedDelta = getGuideSnappedMoveDelta(latest.dx, latest.dy, current, {
      applyGridSnap: false,
    })

    if (smartGuidesEnabledRef.current) {
      const snap = computeMoveSmartGuideSnap(current, snappedDelta, {
        shapes: shapesRef.current,
        getOtherShapeBounds,
        getMovedSelectionBounds,
        effectiveSnapGuideDistance,
        layoutSnapLines: snapToGuidesRef.current ? layoutSnapLines : undefined,
      })

      if (snap) {
        emitSmartGuides(snap.guides)
        snappedDelta = snap.delta
      } else {
        emitSmartGuides([])
      }
    }

    applyLiveMoveUpdates(current, snappedDelta, {
      updateShape: updateShapeRef.current,
      setMultiSelectChrome,
      multiSelectSyncPrevShapesRef,
      getShapes: () => shapesRef.current,
    })

    scheduleLiveSelectionGeometrySync()
    scheduleLiveWrapSync()
  }, [
    effectiveSnapGuideDistance,
    emitSmartGuides,
    getGuideSnappedMoveDelta,
    getMovedSelectionBounds,
    getOtherShapeBounds,
    interactionRef,
    layoutSnapLines,
    multiSelectSyncPrevShapesRef,
    pendingPointerRef,
    scheduleLiveSelectionGeometrySync,
    scheduleLiveWrapSync,
    setMultiSelectChrome,
    shapesRef,
    smartGuidesEnabledRef,
    snapToGuidesRef,
    updateShapeRef,
  ])

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      const interaction = interactionRef.current

      if (!interaction || event.pointerId !== interaction.pointerId) {
        return
      }

      event.preventDefault()
      const scaleFactor = scaleRef.current || 1
      const dx = (event.clientX - interaction.startClientX) / scaleFactor
      const dy = (event.clientY - interaction.startClientY) / scaleFactor

      pendingPointerRef.current = {
        dx,
        dy,
        clientX: event.clientX,
        clientY: event.clientY,
        shiftKey: event.shiftKey,
      }

      if (interaction.type === 'move') {
        if (!interaction.hasDragged && isPastMoveClickThreshold(dx, dy)) {
          interactionRef.current = { ...interaction, hasDragged: true }
          promoteAltDragDuplicateRef.current()
        }

        // An Alt+press may still be a click, so nothing moves until it is a drag: otherwise the
        // original would shift by the first few pixels before the copy takes over.
        if (interactionRef.current?.altDragDuplicatePending && !interactionRef.current.hasDragged) {
          return
        }

        if (rafIdRef.current != null) {
          window.cancelAnimationFrame(rafIdRef.current)
          rafIdRef.current = null
        }

        applyLiveMove()
        return
      }

      if (rafIdRef.current != null) {
        return
      }

      rafIdRef.current = window.requestAnimationFrame(() => {
        rafIdRef.current = null
        const latest = pendingPointerRef.current
        const { current } = interactionRef

        if (!latest || !current) {
          return
        }

        if (current.type === 'resize' && current.direction) {
          const resizeRotation =
            current.unifiedSelectionResizeSession?.chromeStart.rotationDeg ??
            current.initial.rotation ??
            0

          document.body.style.cursor = getResizeCursorForHandle(current.direction, resizeRotation)

          if (current.unifiedSelectionResizeSession) {
            applyMultiSelectResizeStep(
              current.unifiedSelectionResizeSession,
              latest.dx,
              latest.dy,
              latest.shiftKey,
              {
                panelDimensionsLinked: panelDimensionsLinkedRef.current,
                updateShape: updateShapeRef.current,
                setMultiSelectChrome,
                multiSelectChromeRef,
              }
            )
            scheduleLiveSelectionGeometrySync()
            scheduleLiveWrapSync()

            return
          }

          const resized = computeSingleObjectResize(current, current.direction, latest, {
            panelDimensionsLinked: panelDimensionsLinkedRef.current,
            smartGuidesEnabled: smartGuidesEnabledRef.current,
            getOtherShapeBounds,
            effectiveSnapGuideDistance,
            layoutSnapLines: snapToGuidesRef.current ? layoutSnapLines : undefined,
            onGuides: emitSmartGuides,
          })
          const { dir, useAspectRatio } = resized
          const { next: clampedNext, photoFillUpdate } = applyPhotoFillResizeClamp(
            current.initial,
            dir,
            resized.next,
            useAspectRatio
          )
          let next = clampedNext

          if (current.groupTransformInitials) {
            applyLiveGroupResize(
              current as InteractionState &
                Required<Pick<InteractionState, 'groupTransformInitials'>>,
              dir,
              next,
              updateShapeRef.current
            )
          } else {
            next = clampResizeToPasteboardMax(
              {
                initial: current.initial,
                direction: dir,
                next,
                aspectLocked: useAspectRatio,
              },
              resizeMaxDimensionToastActiveRef,
              unitOfMeasure
            )

            updateShapeRef.current(
              current.shapeId,
              photoFillUpdate ? { ...next, ...photoFillUpdate } : next
            )
          }

          scheduleLiveSelectionGeometrySync()
          scheduleLiveWrapSync()
          return
        }

        if (current.type === 'rotate' && current.overlayRect) {
          const cx = current.overlayRect.left + current.overlayRect.width / 2
          const cy = current.overlayRect.top + current.overlayRect.height / 2
          const rotateDeps = {
            getShapes: () => shapesRef.current,
            smartGuidesEnabled: smartGuidesEnabledRef.current,
            emitSmartGuides,
            updateShape: updateShapeRef.current,
            setMultiSelectChrome,
            multiSelectChromeRef,
            scheduleLiveSelectionGeometrySync,
            scheduleLiveWrapSync,
          }

          if (
            current.multiRotateInitials &&
            current.rotatePivotLocal &&
            current.rotationStartAngle !== undefined
          ) {
            applyLiveMultiRotate(
              current as MultiRotateInteraction & { rotationStartAngle: number },
              latest,
              cx,
              cy,
              rotateDeps
            )
          } else {
            applyLiveSingleRotate(current, latest, cx, cy, rotateDeps)
          }
        }
      })
    },
    [
      applyLiveMove,
      resizeMaxDimensionToastActiveRef,
      unitOfMeasure,
      effectiveSnapGuideDistance,
      emitSmartGuides,
      getOtherShapeBounds,
      interactionRef,
      layoutSnapLines,
      multiSelectChromeRef,
      panelDimensionsLinkedRef,
      pendingPointerRef,
      promoteAltDragDuplicateRef,
      rafIdRef,
      scaleRef,
      scheduleLiveSelectionGeometrySync,
      scheduleLiveWrapSync,
      setMultiSelectChrome,
      shapesRef,
      smartGuidesEnabledRef,
      snapToGuidesRef,
      updateShapeRef,
    ]
  )

  return { applyLiveMove, handlePointerMove }
}

export type LiveGestureUpdatesApi = ReturnType<typeof useLiveGestureUpdates>
